import TableContainer from "../_table-container";
import { mapState, mapActions } from "vuex";
import TempAccChart from "./temp-acc-chart";
// Modals
import AddEventModal from "../_modals/add-event";
import AddNoteModal from "../_modals/add-note";
import EditCowModal from "../_modals/edit-cow";
import HeatDiagram from '../_heat-diagram';

import { AlertTypes } from '../../constants/alert_types';
import { AlertStatuses } from '../../constants/alert_statuses';
import i18n from "../../plugins/i18n";
import ReactionModal from "../_modals/reaction";
import UtilsService from "../../services/UtilsService";
import AlertsApi from "../../api/alerts-api";
import ModifyNote from "../_modals/modify-note";

import AlertDetailsService from "../../services/AlertDetailsService";

export default {
  name: "CowProfile",
  components: { TableContainer, TempAccChart, HeatDiagram },
  data() {
    return {
      isModalOpen: false,
      modal: null,
      modalData: {},
      window: 10,
      numberOfLogsToShow: 3,
      neighbours: [null, null],
      selectedTags: [],
      heatIndex: "N/A"
    };
  },
  computed: {
    ...mapState({
      /**
       * Define the profile data of the cow from state
       * @param {object} state The main state object
       */
      profile(state) {
        const profile = state.cows.cow;
        const tags = state.cows.tags && state.cows.tags.filter(({ cows }) => cows.findIndex(cow => cow.cow_id === profile.cow_id) !== -1);

        this.selectedTags = tags.map(({ name }) => name);
        return {
          ...profile,
          heat_index: UtilsService.formatPercentage(profile.heat_index),
          cowStatus: profile.status ? i18n.t(`cowGenerics.cowLogs.${profile.status}`) : i18n.t("cowDetails.unknownState"),
          tags
        };
      },

      /**
       * Define the logs of the the cow from state
       * @param {object} state The main state object
       */
      logs(state) {
        // Get alerts end events, assign category for later use
        const cowState = state.cows.cow;
        const lastHeats = state.alerts.lastHeats;
        if (!cowState.alerts || !cowState.events || !lastHeats)
          return [];

        // Categorize logs
        const alerts = cowState.alerts.map(alert => ({ ...alert, category: 'alert' }));
        const events = cowState.events.map(event => ({ ...event, category: 'event' }));

        // Update alerts with last heats date
        lastHeats.forEach(({ alert_id, last_heat }) => {
          const heatAlert = alerts.find(a => a.alert_id === alert_id);
          if (heatAlert) {
            heatAlert.last_heat = last_heat;
          }
        });

        // Handle logs
        const logs = alerts.concat(events).filter(({ deleted_at }) => !deleted_at);
        const formattedLogs = this.formatLogs(logs);
        const sortedLogs = this.sortLogs(formattedLogs);
        return sortedLogs;
      },

      /**
       * Display only the number of logs defined in the `numberOfLogsToShow` data property
       */
      displayableLogs() {
        // Filtering Out of range, Low coverage, and Network/Gateway failure logs from display

        const logs = this.logs
          .filter(l => !([AlertTypes.LowCoverage, AlertTypes.NetworkFailure, AlertTypes.OutOfRange].includes(l.type) && l.status !== AlertStatuses.active))
          .filter(l => l.status !== 'hidden');

        return logs.slice(0, this.numberOfLogsToShow);
      },
      tagsList(state) {
        return state.cows.tags.map(({ name }) => name);
      }
    })
  },
  methods: {
    ...mapActions("cows", {
      clearCow: "clearCow",
      getCow: "getCow",
      getTags: "getTags",
      getTagsWithCow: "getTagsWithCow",
      assignTags: "assignTags",
      clearTags: "clearTags",
    }),
    ...mapActions("alerts", {
      getLastHeats: "getLastHeats"
    }),
    fillDataOnChart(l) {
      const actDate = l.date;
      const actFromDate = this.$moment(actDate).subtract(3, 'days');
      const actToDate = this.$moment(actDate).add(2, 'days');

      this.$refs.accChart.fetchData(actFromDate, actToDate);
    },
    refreshChart() {
      this.$refs.accChart.resetData();
      this.$refs.accChart.fetchData();
    },
    /**
     * Format data from store to displaying
     * @param {array} data The raw data from store to format
     */
    formatLogs(logs) {
      // Format data
      return logs.map((log) => {
        let logCategorySpecificDatas = {}
        if (log.category === 'alert') {
          const heatPeak = (log.heat_peak && this.$moment(log.heat_peak).add(2, "h").format()) || this.$moment(log.alert_time);
          const isActive = log.status == AlertStatuses.active;
          const hasInseminationWindow = [AlertTypes.Heat, AlertTypes.RepeatHeat].includes(log.type) && isActive;

          logCategorySpecificDatas = {
            id: log.alert_id,
            dateToDisplay: this.$moment(log.alert_time),
            date: this.$moment(log.alert_time),
            updateDate: log.updated_at && this.$moment(log.updated_at),
            heat_peak: heatPeak,
            reacted: log.status == AlertStatuses.confirmed || log.status == AlertStatuses.dismissed || log.status == AlertStatuses.confirmed_with_action,
            isActive: isActive,
            hasInseminationWindow: hasInseminationWindow,
            isAcknowledgable: [AlertTypes.IncreasedActivity, AlertTypes.DecreasedActivity, AlertTypes.LowWaterIntake,
              AlertTypes.HighTemp, AlertTypes.LowTemp].includes(log.type) && isActive,
            confirmLocale: [AlertTypes.Heat, AlertTypes.RepeatHeat].includes(log.type) ? 'heat' : log.type === AlertTypes.Calving ? 'calving' : undefined,
          }
          if (hasInseminationWindow) {
            logCategorySpecificDatas.lastHeat = UtilsService.lastHeat(log);
          }
        } else {
          logCategorySpecificDatas = {
            id: log.event_id,
            dateToDisplay: this.$moment(log.event_time),
            date: log.event_time,
          }
        }

        let details = null;
        if (log.user) {
          const user_name = `${log.user.first_name} ${log.user.last_name}`
          if (log.category === 'alert') {
            if (log.status === AlertStatuses.confirmed) {
              details = `${i18n.t('cowGenerics.details.confirmedBy')} ${user_name}`
            }
            if (log.status === AlertStatuses.dismissed) {
              details = `${i18n.t('cowGenerics.details.dismissedBy')} ${user_name}`
            }
          } else if (log.category === 'event') {
            details = `${i18n.t('cowGenerics.details.addedBy')} ${user_name}`
          }
        } else if (log.status === AlertStatuses.expired) {
          details = `${i18n.t('dashboardView.alerts.expired')}`
        }

        let nameIndex = `cowGenerics.cowLogs.${log.type}`;
        if (log.category === 'alert' && log.type === AlertTypes.Calving)
          nameIndex += "(Alert)";
        return {
          ...logCategorySpecificDatas,
          cow_id: log.cow_id,
          note: log.note,
          details: details,
          status: log.status,
          eartag: this.profile.eartag,
          type: log.type,
          nameOfAction: i18n.t(nameIndex) || i18n.t("cowDetails.unknowActionState"),
        }
      });
    },

    /**
     * Sorts the data by date in descending order (latest in front)
     * @param {array} data data to sort
     */
    sortLogs(data) {
      return data.sort((a, b) => {
        const aDate = a.date;
        const bDate = b.date;
        return this.$moment(bDate).format('x') - this.$moment(aDate).format('x');
      });
    },

    /**
     * Opens the confirmation modal for the actual data
     * @param {event} event The button onClick raw event
     * @param {object} alert The alert data
     */
    async confirmation(event, alert) {
      event.stopPropagation();

      // Set modal data
      this.modalData = {
        alert: alert,
        modalName: alert.type && (i18n.t(`cowGenerics.cowLogs.${alert.type}`) + ` confirmation`),
        reaction: "confirmed",
        reload: async () => { await this.fetchData(alert.cow_id); this.refreshChart(); }
      };

      const dataToSend = {
        confirmation: this.modalData.reaction,
        alert_time: this.$moment().format(),
      };

      // Set loading and wait for data to be sent and response to be returned.
      await AlertsApi.post(this.modalData.alert.id, dataToSend);
      // Reload logs
      this.modalData.reload();
    },
    /**
     * Opens the confirmation with action modal for the actual data
     * @param {event} event The button onClick raw event
     * @param {object} alert The alert data
     */
    confirmAction(event, alert) {
      event.stopPropagation();
      const locale = [AlertTypes.Heat, AlertTypes.RepeatHeat].includes(alert.type) ? 'heat' : alert.type === AlertTypes.Calving ? 'calving' : undefined;
      this.modalData = {
        alert: alert,
        reaction: "confirmed_with_action",
        modalName: locale ? i18n.t(`actions.${locale}.confirmAction`) : i18n.t(`cowGenerics.cowLogs.${alert.type}`),
        reload: async () => { await this.fetchData(alert.cow_id); this.refreshChart(); }
      };
      this.modal = ReactionModal;
      this.isModalOpen = true;
    },

    modifyNote(event, note) {
      event.stopPropagation();

      this.modalData = {
        note: note,
        modalName: "Edit note",
        reload: async () => { await this.fetchData(this.profile.cow_id); this.refreshChart(); }
      };
      this.modal = ModifyNote
      this.isModalOpen = true;
    },

    /**
     * Opens the dismissal modal for the actual data
     * @param {event} event The button onClick raw event
     * @param {object} alert The alert data
     */
    async dismissal(event, alert) {
      event.stopPropagation();

      // Set modal data
      this.modalData = {
        alert: alert,
        modalName: alert.type && (i18n.t(`cowGenerics.cowLogs.${alert.type}`) + ` dismissal`),
        reaction: "dismissed",
        reload: async () => { await this.fetchData(alert.cow_id); this.refreshChart(); }
      };

      const dataToSend = {
        confirmation: this.modalData.reaction,
        alert_time: UtilsService.utcDate(this.$moment()),
      };

      // Set loading and wait for data to be sent and response to be returned.
      await AlertsApi.post(this.modalData.alert.id, dataToSend);
      // Reload logs
      this.modalData.reload();
    },


    /**
     * Open Add Event modal with the cow's ID
     */
    addEvent() {
      this.modalData = {
        cow: { cow_id: this.profile.cow_id, eartag: this.profile.eartag },
        reload: async () => { await this.fetchData(this.profile.cow_id); this.refreshChart(); }
      };
      this.modal = AddEventModal;
      this.isModalOpen = true;
    },

    /**
     * Open Add Note modal with the cow's ID
     */
    addNote() {
      this.modalData = {
        cow: { cow_id: this.profile.cow_id, eartag: this.profile.eartag },
        reload: async () => { await this.fetchData(this.profile.cow_id); this.refreshChart(); }
      };
      this.modal = AddNoteModal;
      this.isModalOpen = true;
    },

    /**
     * Open Edit cow modal with the cow's profile data
     */
    editCow() {
      this.modalData = {
        cow: { ...this.profile },
        reload: () => this.fetchData(this.profile.cow_id)
      };
      this.modal = EditCowModal;
      this.isModalOpen = true;
    },

    /**
     * Increase the number of logs to show
     */
    increseWindow() {
      this.numberOfLogsToShow += this.window;
    },

    /**
     * Fetch data
     */
    async fetchData(cow_id) {
      // clear previous cow data
      this.clearCow();
      // we need to wait until the cow is fetched for chart to be updated correctly (if you wondered why this is here)
      await this.clearCow();
      await this.getCow({ cow_id, with_alerts: true, with_events: true, with_status: true, with_heat_index: true });
      this.get_neighbours(this.$store.state);
      this.getLastHeats();
      this.getTags();
      this.getTagsWithCow();
    },

    get_neighbours(state) {
      const all_cows = state.cows.cowList;
      const cow_id = +this.$route.params.cow_id;
      const alert_id = +this.$route.query.alert_id;
      const ret = [null, null];
      // Find index of current cow
      const index = all_cows.findIndex(c => c.cow_id === cow_id && (!alert_id || alert_id === c.alert_id));

      if (index === -1) return ret;

      // Find previous cow that is not the same
      for (let i = index - 1; i >= 0; i--) {
        if (all_cows[i].cow_id != cow_id) {
          ret[0] = all_cows[i]; break;
        }
      }

      // Find next cow that is not the same
      for (let i = index + 1; i < all_cows.length; i++) {
        if (all_cows[i].cow_id != cow_id) {
          ret[1] = all_cows[i]; break;
        }
      }

      this.neighbours = ret;
    },
    async assign() {
      await this.assignTags({ cow_ids: [this.profile.cow_id], names: this.selectedTags });
      this.fetchData(this.profile.cow_id);
    },
    async clear() {
      await this.clearTags({ cow_ids: [this.profile.cow_id] });
      this.fetchData(this.profile.cow_id);
    },
    navigateToCow(cow) {
      // Navigate to the new cow
      this.$router.push({ 
        name: 'cow-details', 
        params: { cow_id: cow.cow_id }, 
        query: { alert_id: this.$route.query.alert_id && cow.alert_id } 
      });
    },
  },
  watch: {
    /**
     * Watch if route is changed -> Reload cow data
     * @param {object} to The route coming from
     * @param {object} from The route going to
     */
    $route(to, from) {
      const cow_id = to.params.cow_id
      if (cow_id !== from.params.cow_id) {
        this.fetchData(cow_id)
      }
    }
  },
  async mounted() {
    const cow_id = this.$route.params.cow_id;
    this.fetchData(cow_id);
  }
};



// WEBPACK FOOTER //
// ./src/components/_cow-profile/cow-profile.js